import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

/**
 * Validator function that ensures the 'confirmNewPassword' field matches the 'newPassword' field.
 * Should be applied at the FormGroup level.
 *
 * @returns A ValidatorFn that returns a validation error if passwords don't match,
 *          or null if they match or if either field is empty.
 *
 * @example
 * ```typescript
 * const form = new FormGroup({
 *   newPassword: new FormControl(''),
 *   confirmNewPassword: new FormControl('')
 * }, { validators: confirmPasswordValidator() });
 * ```
 */
export function confirmPasswordValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormGroup)) {
      return null;
    }

    const passwordControl = control.get('newPassword');
    const confirmPasswordControl = control.get('confirmNewPassword');

    if (!passwordControl || !confirmPasswordControl) {
      return null;
    }

    const passwordValue = passwordControl.value;
    const confirmPasswordValue = confirmPasswordControl.value;

    if (!passwordValue || !confirmPasswordValue) {
      if (confirmPasswordControl.hasError('mismatch')) {
        const { mismatch: _mismatch, ...remainingErrors } =
          confirmPasswordControl.errors ?? {};
        confirmPasswordControl.setErrors(
          Object.keys(remainingErrors).length ? remainingErrors : null,
        );
      }
      return null;
    }

    if (passwordValue !== confirmPasswordValue) {
      confirmPasswordControl.setErrors({
        ...(confirmPasswordControl.errors ?? {}),
        mismatch: true,
      });
      return { mismatch: true };
    }

    if (confirmPasswordControl.hasError('mismatch')) {
      const { mismatch: _mismatch, ...remainingErrors } =
        confirmPasswordControl.errors ?? {};
      confirmPasswordControl.setErrors(
        Object.keys(remainingErrors).length ? remainingErrors : null,
      );
    }

    return null;
  };
}
